import React, { useCallback, useState, useEffect } from 'react';
import { Panel, FormControl, FormGroup } from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faMinusCircle, faSearch, faTimes, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { get } from 'helpers/api';
import { debounce } from 'lodash';
import qs from 'qs';

interface UserListProps {
  includeUserIds?: number[];
  excludeUserIds?: number[];
  onAddUser?: (id: number) => void;
  onRemoveUser?: (id: number) => void;
}

interface User {
  id: number;
  name: string;
}

export default function UserList({ includeUserIds, excludeUserIds, onAddUser, onRemoveUser }: UserListProps) {
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);

  const searchUsers = useCallback(
    debounce((_searchText: string, _includeUserIds?: number[], _excludeUserIds?: number[]) => {
      const params: { limit: number, [key: string]: any } = { limit: 15 };
      if (_searchText) params['q[first_name_or_last_name_or_login_credential_email_cont]'] = _searchText;
      let queryString = qs.stringify(params);

      if (_includeUserIds) _includeUserIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });
  
      if (_excludeUserIds) _excludeUserIds.forEach((id: number) => {
        queryString += `&q[id_not_in][]=${id}`; 
      });

      get(`/users/search?${queryString}`).then((data: { users: User[] }) => {
        setUsers(data.users);
        setLoading(false);
      });
    }, 400),
    []
  );

  useEffect(() => {
    setLoading(true);

    if (onRemoveUser && !includeUserIds?.length) {
      setUsers([]);
      setLoading(false);
    } else { 
      searchUsers(searchText, includeUserIds, excludeUserIds);
    }
  }, [searchText, includeUserIds, excludeUserIds, searchUsers, onRemoveUser]);

  const emptyText = () => {
    if (loading) return 'Loading...';

    if (onRemoveUser && includeUserIds?.length === 0) return 'Start by adding users';

    return 'No Users Found';
  };

  return (
    <div>
      <FormGroup className="search-container position-relative">
        <div className="icon">
          <FontAwesomeIcon className="text-primary" icon={faSearch} />
        </div>
        <FormControl
          placeholder="Search"
          type="text"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <div className="icon pull-right clickable">
          <FontAwesomeIcon icon={faTimes} onClick={() => setSearchText('')}/>
        </div>
      </FormGroup>
      <Panel className="overflow-auto" style={{ height: 300 }}>
        {users.length > 0 ? (
          <ul className="list-group">
            {users.map(({ name, id }) => (
              <li className="list-group-item" key={id}>
                {name}
                {onAddUser && (
                  <FontAwesomeIcon
                    className="pull-right text-create clickable"
                    icon={loading ? faSpinner : faPlusCircle}
                    name="add-user"
                    spin={loading}
                    onClick={() => onAddUser(id)}
                  />
                )}
                {onRemoveUser && (
                  <FontAwesomeIcon
                    className="pull-right text-red clickable"
                    icon={loading ? faSpinner : faMinusCircle}
                    name="add-user"
                    spin={loading}
                    onClick={() => onRemoveUser(id)}
                  />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div>
            <h2 className="text-muted text-center mt-5 pt-5">
              {emptyText()}
            </h2>
          </div>
        )}
      </Panel>
    </div>
  );
}
